import type { Format } from "../data/schema";

/** Superscript glyphs so we can render exponents without extra markup. */
const SUPERSCRIPT: Record<string, string> = {
  "-": "⁻",
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
};

export function toSuperscript(n: number): string {
  return String(n)
    .split("")
    .map((ch) => SUPERSCRIPT[ch] ?? ch)
    .join("");
}

/** "4.2 × 10⁻⁶" style. Keeps `sig` significant digits in the mantissa. */
export function scientific(value: number, sig = 2): string {
  if (value === 0) return "0";
  if (!isFinite(value)) return "∞";
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = value / Math.pow(10, exponent);
  // Numbers comfortably readable without scientific notation are left plain.
  if (exponent >= -2 && exponent <= 3) {
    return trimNumber(value, sig + 1);
  }
  return `${trimNumber(mantissa, sig)} × 10${toSuperscript(exponent)}`;
}

/** Trim trailing zeros and group thousands for plain numbers. */
export function trimNumber(value: number, sig = 3): string {
  if (!isFinite(value)) return "∞";
  if (value === 0) return "0";
  const abs = Math.abs(value);
  // Choose decimal places based on magnitude so small values keep precision.
  let decimals: number;
  if (abs >= 100) decimals = 0;
  else if (abs >= 1) decimals = Math.max(0, sig - 1);
  else decimals = Math.min(6, sig - Math.floor(Math.log10(abs)) - 1);
  const fixed = value.toFixed(Math.max(0, decimals));
  // Strip trailing zeros, then group integer part with thousands separators.
  const trimmed = parseFloat(fixed).toString();
  const [intPart, fracPart] = trimmed.split(".");
  const grouped = Number(intPart).toLocaleString("en-US");
  return fracPart ? `${grouped}.${fracPart}` : grouped;
}

/** Probability shown smartly: percentages for common odds, "1 in N" for rare. */
export function probability(value: number): string {
  if (value <= 0) return "≈ 0";
  if (value >= 1) return "100%";
  if (value >= 0.01) return `${trimNumber(value * 100, 3)}%`;
  // Rare events read better as "1 in N".
  const denom = Math.round(1 / value);
  return `1 in ${denom.toLocaleString("en-US")}`;
}

export function percent(value: number): string {
  return `${trimNumber(value * 100, 3)}%`;
}

/** Format a value according to a factor's declared `format`. */
export function formatValue(value: number, format: Format): string {
  switch (format) {
    case "probability":
      return probability(value);
    case "percent":
      return percent(value);
    case "scientific":
      return scientific(value);
    case "number":
    default:
      return trimNumber(value);
  }
}
